/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useRef } from 'react';

import { useIntersection } from '@common/hooks/useIntersection';
import { sendGAEvent } from '@common/utlis';
import Button from '@stories/atoms/Button';
import Alert from '@stories/atoms/Icons/alert.svg';

import BackIcon from './icons/back.svg';
import Check from './icons/check.svg';

import type { OrderStatusDto } from '@vitafy/storefront-api-contracts-fetch';
import type { FC } from 'react';

const updatesStyles = {
  entry: (t) =>
    css({
      color: t.color.grey.g60,
      display: 'flex',
      gap: '1rem',
      position: 'relative'
    }),
  entryActive: css({
    display: 'flex',
    fontWeight: 'bold',
    gap: '1rem',
    position: 'relative'
  }),
  entryDone: css({
    display: 'flex',
    gap: '1rem',
    position: 'relative'
  }),
  entryLeft: (t) =>
    css({
      '::before': {
        backgroundColor: t.color.grey.g72,
        bottom: '0',
        content: '""',
        left: '11px',
        position: 'absolute',
        top: '0',
        width: '2px'
      }
    }),
  entryLeftActive: (t) =>
    css({
      '::before': {
        backgroundColor: t.color.primary,
        bottom: '0',
        content: '""',
        left: '11px',
        position: 'absolute',
        top: '0',
        width: '2px'
      }
    }),
  span: (t) =>
    css({
      alignItems: 'center',
      backgroundColor: t.color.grey.g72,
      borderRadius: '50%',
      display: 'flex',
      justifyContent: 'center',
      maxWidth: '1.5rem',
      maxHeight: '1.5rem',
      padding: '0.25rem',
      position: 'relative',
      zIndex: '1'
    }),
  spanActive: (t) =>
    css({
      alignItems: 'center',
      backgroundColor: t.color.primary,
      borderRadius: '50%',
      display: 'flex',
      justifyContent: 'center',
      maxWidth: '1.5rem',
      maxHeight: '1.5rem',
      padding: '0.25rem',
      position: 'relative',
      zIndex: '1'
    }),
  entryText: css({
    marginBottom: '2rem'
  })
};

const newEntry = (message: string, status: string, active_status: string) => {
  const entryStyles = () => {
    if (active_status == 'active') {
      return updatesStyles.entryActive;
    } else if (active_status == '') {
      return updatesStyles.entry;
    } else {
      return updatesStyles.entryDone;
    }
  };
  return (
    <div css={entryStyles}>
      <div
        css={
          status != 'delivered'
            ? active_status == ''
              ? updatesStyles.entryLeft
              : updatesStyles.entryLeftActive
            : null
        }
      >
        <span css={active_status == '' ? updatesStyles.span : updatesStyles.spanActive}>
          <Check />
        </span>
      </div>
      <div css={status != 'delivered' ? updatesStyles.entryText : null}>
        <p>{message}</p>
      </div>
    </div>
  );
};

type TOrderTrackingResults = {
  setPage: (par: string) => void;
  trackingData: OrderStatusDto;
};

const OrderTrackingResults: FC<TOrderTrackingResults> = ({ setPage, trackingData }) => {
  const sectionRef = useRef(null);
  useIntersection(sectionRef, () =>
    sendGAEvent({ category: 'Order Tracking', action: 'Module Impression', label: 'Order Tracking Results' })
  );
  const currentStatus = {
    PaymentReceived: trackingData.paymentStatus != 'paid' ? '' : 'done',
    Processing:
      trackingData.paymentStatus == 'paid' && !trackingData.trackingNumber
        ? 'active'
        : trackingData.trackingNumber
          ? 'done'
          : '',
    Transit:
      trackingData.trackingNumber && trackingData.isOrderDelivered != true
        ? 'active'
        : trackingData.isOrderDelivered == true
          ? 'done'
          : '',
    Delivered: trackingData.isOrderDelivered == true ? 'active' : ''
  };
  const parsedCreatedDate = new Date(trackingData.createdAt);
  const formattedCreatedDate = parsedCreatedDate.toLocaleDateString('en-GB').replace(/\//g, '.');
  return (
    <div
      ref={sectionRef}
      css={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '3rem'
      }}
    >
      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          maxWidth: '462px',
          width: '100%'
        }}
      >
        <div
          css={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '1rem',
            position: 'relative',
            width: '100%'
          }}
        >
          <a
            href="#"
            css={{
              alignItems: 'center',
              color: 'inherit',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'flex-start',
              gap: '4px',
              textDecoration: 'none',
              width: '100%'
            }}
            onClick={(e) => {
              e.preventDefault();
              sendGAEvent({ category: 'Order Tracking', action: 'Click', label: 'zuruck button' });
              setPage('trackingForm');
            }}
          >
            <BackIcon css={{ width: '18px', height: '18px' }} />
            <span>
              <b>Zurück</b>
            </span>
          </a>

          <h1
            css={(t) => ({
              ...t.headline.h3
            })}
          >
            Bestellstatus
          </h1>
        </div>
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem'
          }}
        >
          <p
            css={{
              lineHeight: '140%'
            }}
          >
            Hallo {trackingData.firstname}, <br />
            hier is der Status deiner Bestellung <b>{trackingData.orderNumber}</b> vom{' '}
            <b>{formattedCreatedDate}</b>.
          </p>
          {trackingData.isCanceled == true && (
            <div
              css={(t) => ({
                color: t.color.error,
                display: 'flex',
                fontWeight: 'bold',
                gap: '0.5rem',
                justifyContent: 'flex-start',
                width: '100%'
              })}
            >
              <Alert css={{ minWidth: '20px', minHeight: '20px' }} />
              <p>Deine Bestellung wurde storniert.</p>
            </div>
          )}
          {trackingData.paymentStatus != 'paid' && trackingData.isCanceled != true && (
            <div
              css={(t) => ({
                color: t.color.error,
                display: 'flex',
                fontWeight: 'bold',
                gap: '0.5rem',
                justifyContent: 'flex-start',
                width: '100%'
              })}
            >
              <Alert css={{ minWidth: '20px', minHeight: '20px' }} />
              <p>
                Deine Bestellung ist noch unbezahlt. Sobald die Zahlung bei uns eingegangen ist, kannst du
                hier den Status deiner Bestellung einsehen.
              </p>
            </div>
          )}
          {trackingData.paymentStatus == 'paid' &&
            !trackingData.trackingNumber &&
            trackingData.isCanceled != true && (
              <p
                css={{
                  fontWeight: 'bold'
                }}
              >
                Voraussichtlicher Versand: {trackingData.expectedShipping}
              </p>
            )}
          {/* {trackingData.isOrderDelivered == false && trackingData.trackingNumber && (
            <p
              css={{
                fontWeight: 'bold'
              }}
            >
              Voraussichtliches Lieferdatum: zwischen {trackingData.expectedDelivery?.earliestDeliveryData}{' '}
              und {trackingData.expectedDelivery?.latestDeliveryDate}
            </p>
          )} */}
          {trackingData.paymentStatus == 'paid' && trackingData.isCanceled != true && (
            <div>
              {newEntry(`Zahlung eingegangen`, 'paymentReceived', currentStatus.PaymentReceived)}
              {newEntry(`In Bearbeitung`, 'processing', currentStatus.Processing)}
              {newEntry(`Unterwegs mit DHL`, 'transit', currentStatus.Transit)}
              {newEntry('Zugestellt', 'delivered', currentStatus.Delivered)}
            </div>
          )}
        </div>

        {trackingData.trackingNumber && (
          <div>
            {Array.isArray(trackingData.trackingNumber) && trackingData.trackingNumber.length > 1 ? (
              <div>
                <p>
                  Deine Bestellung enthält mehrere Pakete, hier sind die Sendungsnummern:{' '}
                  {trackingData.trackingNumber.map((n, index) => (
                    <span key={n}>
                      <a
                        css={(t) => ({
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          ':hover': {
                            color: t.color.primary
                          }
                        })}
                        href={trackingData.trackingUrl?.[index]}
                        target="_blank"
                        onClick={() =>
                          sendGAEvent({
                            category: 'Order Tracking',
                            action: 'Click',
                            label: 'tracking URL clicked'
                          })
                        }
                      >
                        <b>{n}</b>
                      </a>
                      {index < (trackingData?.trackingNumber?.length || 0) - 1 && ', '}
                    </span>
                  ))}
                </p>
              </div>
            ) : (
              <div
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '1rem',
                  width: '100%'
                }}
                onClick={() =>
                  sendGAEvent({
                    category: 'Order Tracking',
                    action: 'Click',
                    label: 'ZUM DHL TRACKING'
                  })
                }
              >
                <Button
                  attrs={{ type: 'button', target: '_blank' }}
                  buttonStyle="secondary"
                  cssProps={{ width: '225px' }}
                  label="ZUM DHL TRACKING"
                  href={
                    trackingData.trackingUrl?.[0] ??
                    'https://www.dhl.de/en/privatkunden/dhl-sendungsverfolgung.html'
                  }
                />
              </div>
            )}
          </div>
        )}

        <div>
          <p
            css={{
              textAlign: 'center'
            }}
          >
            Hast du noch Fragen?
            <br /> Dann schreibe uns gerne an!
          </p>
          <div
            css={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              marginTop: '1rem'
            }}
            onClick={() =>
              sendGAEvent({
                category: 'Order Tracking',
                action: 'Click',
                label: 'Hilfe & Kontakt - Results Page'
              })
            }
          >
            <Button
              attrs={{ type: 'button', target: '_blank' }}
              buttonStyle="primary"
              href="/faq"
              label="HILFE & KONTAKT"
              outline
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingResults;
